import React, { useState, useEffect, useRef } from 'react';
import {
  TextField, Button, Select, MenuItem, InputLabel, FormControl, Grid, Box,
  Autocomplete, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert, Checkbox,
  FormControlLabel, Paper, useMediaQuery, useTheme, Card, CardContent, Chip, Divider,
  Tooltip, Switch, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from "../context/AuthContext";
import { MacroIcon } from '../assets/icons';
// Icons
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import BlenderIcon from '@mui/icons-material/Blender';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ListItemIcon, ListItemText } from '@mui/material';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import IngredientItem from './Common/IngredientItem';
import SuccessDialog from './Common/SuccessDialog';
import TaxonomyDialog from './Common/TaxonomyDialog';
import ImageCropDialog from './Common/ImageCropDialog';
import { fetchWithTimeout, getApiErrorMessage } from '../utils/apiErrors';
import { normalizeImageFile } from '../utils/imageProcessing';
import AddIcon from '@mui/icons-material/Add';

// --- Styled Components ---

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1600px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid #f1f5f9',
  height: '100%',
  overflow: 'visible'
}));

const CardHeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: '#1e293b',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2)
}));

const ImageUploadBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'image'
})(({ theme, image }) => ({
  width: '100%',
  aspectRatio: '4 / 3',
  borderRadius: '20px',
  border: image ? 'none' : '2px dashed #cbd5e1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: image ? 'transparent' : '#f8fafc',
  backgroundImage: image ? `url(${image})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#667eea',
    backgroundColor: image ? 'transparent' : '#f1f5f9'
  }
}));

const StepNumber = styled(Box)(({ theme }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  backgroundColor: '#667eea',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  marginTop: '6px'
}));

const MacroChip = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  backgroundColor: color || '#f1f5f9',
  borderRadius: '12px',
  minWidth: '80px',
  '& .label': { fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' },
  '& .value': { fontWeight: 'bold', color: '#1e293b' }
}));

const ComponentRow = styled(Grid)(({ theme }) => ({
  transition: 'background-color 0.2s',
  borderRadius: '12px',
  padding: '8px',
  '&:hover': {
    backgroundColor: '#f8fafc'
  }
}));

const ToggleChip = styled(Chip)(({ theme, selected }) => ({
  borderRadius: '12px',
  fontWeight: 600,
  backgroundColor: selected ? '#667eea' : '#f1f5f9',
  color: selected ? '#fff' : '#64748b',
  border: 'none',
  '&:hover': {
    backgroundColor: selected ? '#5a67d8' : '#e2e8f0'
  }
}));

// --- Main Component ---

const NuevaReceta = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams(); // Check for ID to enable Edit Mode
  const { authToken, userRole } = useAuth();
  const isAdmin = userRole === 'admin';
  const isEditMode = Boolean(id);

  // --- State ---
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [name, setName] = useState('');
  // Unified components state: { refModel, refId, quantity, unit, quantityMode }
  const [components, setComponents] = useState([{ refModel: 'Ingredient', refId: '', quantity: '', unit: 'gramos', quantityMode: 'total' }]);
  const [steps, setSteps] = useState(['']);
  const [servings, setServings] = useState('');
  const [prepTime, setPrepTime] = useState({ hours: '', minutes: '' });
  const [tipoDeCocina, setTipoDeCocina] = useState([]);
  const [restricciones, setRestricciones] = useState([]);
  const [categoriaRecetas, setCategoriaRecetas] = useState([]);
  const [momentoDelDia, setMomentoDelDia] = useState([]);
  const [tipoPlato, setTipoPlato] = useState([]); // Array of strings
  const [difficulty, setDifficulty] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [revisado, setRevisado] = useState(false);
  const [createdBy, setUserId] = useState(null);

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState(null); // Object URL de la imagen seleccionada
  const [openCropDialog, setOpenCropDialog] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Data Sources
  const [allIngredients, setAllIngredients] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]); // Sub-recipes
  const [allCategoriaRecetas, setAllCategoriaRecetas] = useState([]);
  const [categories, setCategories] = useState([]); // For new ingredient dialog
  const [allRestricciones, setAllRestricciones] = useState([]);
  const [allTipoDeCocina, setAllTipoDeCocina] = useState([]);

  // Constant Options
  const MOMENTOS_DEL_DIA = ['Desayuno', 'Comida', 'Cena', 'Snack'];
  const TIPOS_DE_PLATO = ['Desayuno', 'Snack', 'Entrante', 'Principal', 'Postre', 'Guarnición', 'Bebidas'];

  // Temporary/UI State
  const [totalMacronutrients, setTotalMacronutrients] = useState(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [message, setMessage] = useState(null); // Used for Error Dialog
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  // Campos con error, para marcarlos en el formulario. Claves: 'name', 'servings',
  // 'components.<fila>.<campo>'. El valor es el texto de ayuda a mostrar.
  const [fieldErrors, setFieldErrors] = useState({});
  const hasFetchedUser = useRef(false);

  // Taxonomy Dialogs State
  const [openTaxonomyDialog, setOpenTaxonomyDialog] = useState({ open: false, type: '', endpoint: '', field: 'name', title: '', setter: null, currentList: [] });

  // New Ingredient Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [ingrediente, setIngrediente] = useState({
    name: '',
    kilocalories: '',
    carbs: '',
    protein: '',
    fat: '',
    categoria: '',
    subcategoria: '',
    standardUnit: 'gramos',
    gramsPerUnit: 1,
    ingredientCategories: [],
    allergens: [],
    revisado: false,
    createdBy: ''
  });

  const [allIngredientCategories, setAllIngredientCategories] = useState([]);
  const [allAllergens, setAllAllergens] = useState([]);

  // --- Effects ---
  const location = useLocation();

  // Fetch User
  useEffect(() => {
    if (authToken && !hasFetchedUser.current) {
      const fetchUser = async () => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/user`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            setUserId(data.id);
            if (!isEditMode) {
              setIsLoading(false);
            }
          }
        } catch (e) { console.error(e); }
      };
      fetchUser();
      hasFetchedUser.current = true;
    }
  }, [authToken, isEditMode]);

  // Fetch Initial Data (Taxonomies and optionally Recipe)
  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Fetch Taxonomies
        const [ing, recipes, catRec, cats, restr, types, catIng, allerg] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/ingredients`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/recipes`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/categoriaRecetas`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/categorias`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/restricciones`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/tipoDeCocina`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/ingredient-categories`).then(r => r.json()),
          fetch(`${import.meta.env.VITE_API_URL}/allergens`).then(r => r.json()),
        ]);

        setAllIngredients(ing);
        setAllRecipes(recipes);
        setAllCategoriaRecetas(catRec);
        setCategories(cats);
        setAllRestricciones(restr);
        setAllTipoDeCocina(types);
        setAllIngredientCategories(catIng || []);
        setAllAllergens(allerg || []);

        // 2. Fetch Recipe if Edit Mode
        if (isEditMode) {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${id}`);
          if (res.ok) {
            const recipe = await res.json();
            populateForm(recipe, ing, recipes, catRec, restr, types);
          } else {
            setMessage("No se pudo cargar la receta.");
            setOpenErrorDialog(true);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, isEditMode]);

  // Populate validation logic
  useEffect(() => {
    if (location.state?.recipeData && !isLoading && allIngredients.length > 0) {
      const r = location.state.recipeData;
      setName(r.name || '');
      setServings(r.servings || '');
      setPrepTime(r.prepTime || { hours: '', minutes: '' });
      setDifficulty(r.difficulty || '');
      setExternalUrl(r.externalUrl || '');
      setSteps(r.steps && r.steps.length > 0 ? r.steps : ['']);

      // Arrays (Simple strings)
      if (r.tipoPlato) setTipoPlato(r.tipoPlato);
      if (r.momentoDelDia) setMomentoDelDia(r.momentoDelDia);
      // Note: Restrictions/KitchenType are complex to map if AI sends strings. 
      // For now AI sends raw strings maybe? My prompt asked for strings?
      // My prompt asked for specific ENUMs but they might not match IDs.
      // I will skipping complex taxonomy mapping for now to avoid errors, or try simple name matching.

      // Components (Ingredients)
      if (r.components) {
        const mapped = r.components.map(c => ({
          refModel: 'Ingredient',
          refId: c.refId || '', // Empty if new
          quantity: c.quantity,
          unit: c.unit,
          quantityMode: 'total',
          // Custom field for UI to show it's a proposed ingredient
          proposedName: c.matchType === 'new' ? c.name : undefined,
          proposedData: c.proposedData,
          inputValue: c.matchType === 'new' ? c.name : undefined // Init autocomplete text
        }));
        setComponents(mapped);
      }

      // Clear state to avoid re-populating on refresh if desired, or keep it.
      // window.history.replaceState({}, document.title);
    }
  }, [location.state, isLoading, allIngredients]);

  // Populate Form Helper
  const populateForm = (r, ingList, recList, catRecList, restrList, typeList) => {
    setName(r.name || '');
    setServings(r.servings || '');
    setPrepTime(r.prepTime || { hours: '', minutes: '' });
    setDifficulty(r.difficulty || '');
    setExternalUrl(r.externalUrl || '');
    setMainImage(r.mainImage || '');
    setRevisado(r.revisado || false);
    setSteps(r.steps && r.steps.length > 0 ? r.steps : ['']);

    // Arrays
    setMomentoDelDia(r.momentoDelDia || []);
    setTipoPlato(r.tipoPlato || []);

    // Object Arrays (Map IDs to Objects)
    setTipoDeCocina(r.tipoDeCocina?.map(id => typeList.find(t => t._id === (typeof id === 'object' ? id._id : id))).filter(Boolean) || []);
    setRestricciones(r.restricciones?.map(id => restrList.find(re => re._id === (typeof id === 'object' ? id._id : id))).filter(Boolean) || []);
    setCategoriaRecetas(r.categoriaRecetas?.map(id => catRecList.find(c => c._id === (typeof id === 'object' ? id._id : id))).filter(Boolean) || []);

    // Components: Merge Legacy Ingredients + New Components
    let mergedComponents = [];

    // 1. Add Legacy Ingredients if they exist
    if (r.ingredients && r.ingredients.length > 0) {
      mergedComponents = r.ingredients.map(i => ({
        refModel: 'Ingredient',
        refId: i.ingredientId?._id || i.ingredientId, // Handle populated or raw ID
        quantity: i.quantity,
        unit: i.unit,
        quantityMode: 'total'
      }));
    }

    // 2. Add New Components
    if (r.components && r.components.length > 0) {
      const mappedComps = r.components.map(c => ({
        refModel: c.refModel,
        refId: c.refId?._id || c.refId,
        quantity: c.quantity,
        unit: c.unit,
        quantityMode: c.quantityMode || 'total',
        expandInShoppingList: c.expandInShoppingList
      }));
      mergedComponents = [...mergedComponents, ...mappedComps];
    }

    if (mergedComponents.length === 0) {
      mergedComponents = [{ refModel: 'Ingredient', refId: '', quantity: '', unit: 'gramos', quantityMode: 'total' }];
    }

    setComponents(mergedComponents);

    // Trigger macro calc
    // We need to wait for state update or pass directly
    // updateMacros(mergedComponents, r.servings); // Will happen via effect or manual call? 
    // Better to call it directly here with the data we have
    // But updateMacros uses state... so we might need a useEffect or just let the handleComponentChange do it? 
    // Actually updateMacros depends on FETCHING from backend.
    // Let's call it manually
  };

  // Calculate macros when components or servings load/change
  useEffect(() => {
    if (components.length > 0 && servings && !isLoading) {
      // Debounce slightly to avoid double calls on load
      const timer = setTimeout(() => updateMacros(components, servings), 500);
      return () => clearTimeout(timer);
    }
  }, [components, servings, isLoading]);


  // --- Logic & Handlers ---

  // Generate valid Image URL for preview
  const getPreviewImage = (path) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;
    const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
    const cleanPath = path.replace(/\\/g, '/').replace(/^\//, '');
    return `${baseUrl}/${cleanPath}`;
  };

  // Macro Calculation using Backend
  const updateMacros = async (currentComponents, currentServings) => {
    if (!currentServings || currentComponents.length === 0) {
      setTotalMacronutrients(null);
      return;
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/recipes/compute-macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ingredients: [], // We only use components in this new model
          components: currentComponents,
          servings: currentServings,
          totalYield: 0
        })
      });
      const data = await response.json();
      setTotalMacronutrients(data.macronutrients || null);
    } catch (error) {
      console.error('Error fetching macros:', error);
    }
  };

  // Toggle multi-select chips
  const toggleSelection = (currentList, item, setter) => {
    if (currentList.includes(item)) {
      setter(currentList.filter(i => i !== item));
    } else {
      setter([...currentList, item]);
    }
  };

  // Quita la marca de error de un campo en cuanto el usuario lo corrige.
  const clearFieldError = (...keys) => {
    setFieldErrors(prev => {
      if (!keys.some(k => k in prev)) return prev;
      const next = { ...prev };
      keys.forEach(k => delete next[k]);
      return next;
    });
  };

  // Component Handlers
  const handleComponentChange = (index, field, value) => {
    const newComps = [...components];
    let updatedComp = { ...newComps[index], [field]: value };

    // Reset fields when type changes
    if (field === 'refModel') {
      updatedComp.refId = '';
      updatedComp.unit = value === 'Recipe' ? 'raciones' : 'gramos';
      if (value === 'Recipe') updatedComp.quantityMode = 'total';
    }

    // Auto-select unit when Ingredient changes
    if (field === 'refId' && updatedComp.refModel === 'Ingredient') {
      const ing = allIngredients.find(i => i._id === value);
      if (ing) {
        updatedComp.unit = ing.standardUnit || 'gramos';
      }
    }

    newComps[index] = updatedComp;
    setComponents(newComps);
    // Elegir componente reescribe también su unidad, así que sus marcas caducan.
    const clearedKeys = [`components.${index}.${field}`];
    if (field === 'refId' || field === 'refModel') clearedKeys.push(`components.${index}.refId`, `components.${index}.refModel`, `components.${index}.unit`);
    clearFieldError(...clearedKeys);
  };

  const handleComponentDelete = (index) => {
    const newComps = components.filter((_, i) => i !== index);
    setComponents(newComps);
  };

  const handleAddComponent = () => {
    setComponents([...components, { refModel: 'Ingredient', refId: '', quantity: '', unit: 'gramos', quantityMode: 'total' }]);
  };

  // New Ingredient Dialog Handlers
  // New Ingredient Dialog Handlers
  const handleCreateIngredient = async () => {
    // Basic validation
    if (!ingrediente.name || !ingrediente.categoria) {
      // You might want to show an error message here
      return;
    }

    // Find the category to get a valid default subcategory if missing
    const selectedCat = categories.find(c => c._id === ingrediente.categoria);
    const validSubcat = ingrediente.subcategoria || (selectedCat && selectedCat.subcategorias && selectedCat.subcategorias[0]) || 'General';

    const payload = {
      ...ingrediente,
      carbs: Number(ingrediente.carbs) || 0,
      protein: Number(ingrediente.protein) || 0,
      fat: Number(ingrediente.fat) || 0,
      kilocalories: Number(ingrediente.kilocalories) || 0,
      subcategoria: validSubcat,
      ingredientCategories: (ingrediente.ingredientCategories || []).map(c => c._id || c),
      allergens: (ingrediente.allergens || []).map(a => a._id || a),
      revisado: ingrediente.revisado,
      standardUnit: ingrediente.standardUnit,
      gramsPerUnit: Number(ingrediente.gramsPerUnit) || 1,
      createdBy
    };

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setOpenSnackbar(true);
        setOpenDialog(false);
        const ingRes = await fetch(`${import.meta.env.VITE_API_URL}/ingredients`);
        const ingData = await ingRes.json();
        setAllIngredients(ingData);
        // Reset form
        // Reset form
        setIngrediente({
          name: '',
          kilocalories: '',
          carbs: '',
          protein: '',
          fat: '',
          categoria: '',
          subcategoria: '',
          standardUnit: 'gramos',
          gramsPerUnit: 1,
          ingredientCategories: [],
          allergens: [],
          revisado: false,
          createdBy: ''
        });
      } else {
        console.error("Failed to create ingredient", await res.text());
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Helper to open taxonomy dialogs
  const openTaxDialog = (type, endpoint, title, setter, currentList, field = 'name') => {
    setOpenTaxonomyDialog({ open: true, type, endpoint, title, setter, currentList, field });
  };

  // Image Upload — al seleccionar archivo, abrimos el editor de recorte
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const normalizedFile = await normalizeImageFile(file, {
        maxDimension: 2560,
        quality: 0.9,
        maxOutputBytes: 4 * 1024 * 1024
      });
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      const objectUrl = URL.createObjectURL(normalizedFile);
      setCropSrc(objectUrl);
      setOpenCropDialog(true);
    } catch (imageError) {
      setMessage(imageError.message || 'No se pudo preparar la imagen seleccionada.');
      setOpenErrorDialog(true);
    } finally {
      setUploadingImage(false);
      // Permite volver a seleccionar el mismo archivo si se cancela.
      e.target.value = '';
    }
  };

  // Subimos el blob ya recortado (relación 4:3)
  const handleCropConfirm = async (blob) => {
    const formData = new FormData();
    formData.append('image', blob, 'receta.jpg');
    try {
      setUploadingImage(true);
      const res = await fetchWithTimeout(`${import.meta.env.VITE_API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      }, 30000);
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'No se pudo subir la foto de la receta.'));
      }

      const data = await res.json();
      if (!data.path) throw new Error('El servidor no devolvió la dirección de la imagen.');
      setMainImage(data.path);
      handleCropCancel();
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCropCancel = () => {
    setOpenCropDialog(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  // Step Handlers
  const handleStepChange = (index, val) => {
    const newSteps = [...steps];
    newSteps[index] = val;
    setSteps(newSteps);
  };
  const handleAddStep = () => setSteps([...steps, '']);
  const handleRemoveStep = (index) => setSteps(steps.filter((_, i) => i !== index));

  // --- Validación y traducción de errores ---

  // Etiquetas visibles de cada campo del formulario, para nombrar el problema tal
  // y como el usuario lo ve en pantalla.
  const FIELD_LABELS = {
    name: 'Nombre',
    servings: 'Raciones',
    difficulty: 'Dificultad',
    momentoDelDia: 'Momento del día',
    tipoPlato: 'Tipo de plato',
    tipoDeCocina: 'Tipo de cocina',
    restricciones: 'Restricciones',
    categoriaRecetas: 'Categorías',
    steps: 'Pasos',
    externalUrl: 'URL',
    mainImage: 'Foto',
    'prepTime.hours': 'Tiempo (horas)',
    'prepTime.minutes': 'Tiempo (minutos)'
  };

  const COMPONENT_FIELD_LABELS = {
    refId: 'ingrediente o sub-receta',
    refModel: 'tipo de componente',
    quantity: 'cantidad',
    unit: 'unidad',
    quantityMode: 'modo de cantidad'
  };

  // Describe una fila de la lista de ingredientes por su posición y su nombre.
  const describeComponentRow = (rowIndex, comps = components) => {
    const comp = comps[rowIndex];
    if (!comp) return `Fila ${rowIndex + 1}`;
    const label = comp.refModel === 'Recipe'
      ? allRecipes.find(r => r._id === comp.refId)?.name
      : (allIngredients.find(i => i._id === comp.refId)?.name || comp.proposedName || comp.inputValue);
    return label ? `Fila ${rowIndex + 1} (${label})` : `Fila ${rowIndex + 1}`;
  };

  // Motivo del rechazo según el tipo de validación que ha fallado en Mongoose.
  const describeValidationKind = (err) => {
    const hasValue = err.value !== undefined && err.value !== null && err.value !== '';
    // Un valor vacío también se nombra: si no, el aviso señala un campo en el que
    // no se ve nada raro y no hay nada que corregir a mano.
    const shown = hasValue ? ` («${String(err.value)}»)` : ' (vacío)';
    switch (err.kind) {
      case 'required': return 'falta este dato';
      case 'enum': return `valor no admitido${shown}`;
      case 'Number':
      case 'ObjectId':
      case 'cast': return `formato no válido${shown}`;
      case 'min':
      case 'max': return `fuera del rango permitido${shown}`;
      default: return err.message || 'valor no válido';
    }
  };

  // Convierte los errores estructurados del servidor en problemas legibles y en
  // claves de campo para marcar el formulario. rowIndexes traduce el índice del
  // payload enviado a la fila real del formulario.
  const translateServerErrors = (errors, rowIndexes = []) => {
    const problems = [];
    const fields = {};

    errors.forEach(err => {
      const reason = describeValidationKind(err);
      const compMatch = /^components\.(\d+)\.(.+)$/.exec(err.path || '');

      if (compMatch) {
        const payloadIndex = Number(compMatch[1]);
        const field = compMatch[2];
        const rowIndex = rowIndexes[payloadIndex] !== undefined ? rowIndexes[payloadIndex] : payloadIndex;
        const fieldLabel = COMPONENT_FIELD_LABELS[field] || field;
        problems.push(`${describeComponentRow(rowIndex)} · ${fieldLabel}: ${reason}`);
        fields[`components.${rowIndex}.${field}`] = reason;
        return;
      }

      // Los campos de tipo lista llegan indexados (momentoDelDia.0, steps.2…)
      const basePath = (err.path || '').replace(/\.\d+$/, '');
      const label = FIELD_LABELS[err.path] || FIELD_LABELS[basePath] || err.path || 'Campo desconocido';
      problems.push(`${label}: ${reason}`);
      if (FIELD_LABELS[basePath]) fields[basePath] = reason;
    });

    return { problems, fields };
  };

  // Mensaje final del diálogo: una línea por problema detectado.
  const buildProblemsMessage = (problems, intro) => {
    const body = problems.length === 1
      ? problems[0]
      : problems.map(p => `• ${p}`).join('\n');
    return `${intro}\n\n${body}`;
  };

  // Revisa el formulario completo antes de enviarlo y devuelve todos los
  // problemas de una vez, no solo el primero.
  const validateForm = (mappedComponents) => {
    const problems = [];
    const fields = {};

    if (!name.trim()) {
      problems.push('Nombre: la receta necesita un nombre');
      fields.name = 'La receta necesita un nombre';
    }
    if (!servings || isNaN(servings)) {
      problems.push('Raciones: introduce un número válido');
      fields.servings = 'Introduce un número válido';
    }

    // Las filas sin selección se ignoran al guardar; las seleccionadas sin
    // cantidad válida (> 0) bloquean el guardado.
    mappedComponents.forEach((c, idx) => {
      if (!c.refId) return;
      const qty = Number(c.quantity);
      if (c.quantity === '' || c.quantity === null || c.quantity === undefined || isNaN(qty) || qty <= 0) {
        problems.push(`${describeComponentRow(idx)} · cantidad: debe ser un número mayor que 0`);
        fields[`components.${idx}.quantity`] = 'Debe ser mayor que 0';
      }
      if (!c.unit) {
        problems.push(`${describeComponentRow(idx)} · unidad: falta este dato`);
        fields[`components.${idx}.unit`] = 'Falta la unidad';
      }
    });

    return { problems, fields };
  };

  // Submit
  const handleSubmit = async () => {
    // Normalizar componentes (reparando refId por nombre cuando falta)
    const mappedComponents = components.map(c => {
      // Attempt to repair missing refId if we have a name match in ingredients
      let effectiveRefId = c.refId;
      let effectiveUnit = c.unit;

      if (!effectiveRefId && (c.inputValue || c.proposedName)) {
        const txt = (c.inputValue || c.proposedName).toLowerCase();
        const match = allIngredients.find(i => i.name.toLowerCase() === txt);
        if (match) {
          effectiveRefId = match._id;
          if (!effectiveUnit) effectiveUnit = match.standardUnit || 'gramos';
        }
      }

      return {
        refModel: c.refModel,
        refId: effectiveRefId,
        quantity: c.quantity,
        unit: effectiveUnit,
        quantityMode: c.quantityMode,
        expandInShoppingList: false
      };
    });

    const { problems, fields } = validateForm(mappedComponents);
    setFieldErrors(fields);
    if (problems.length > 0) {
      setMessage(buildProblemsMessage(
        problems,
        problems.length === 1
          ? 'Falta un dato para poder guardar la receta:'
          : `Hay ${problems.length} datos que impiden guardar la receta:`
      ));
      setOpenErrorDialog(true);
      return;
    }

    // Filas que se envían al servidor, guardando de qué fila del formulario sale
    // cada una para poder señalar el campo exacto si el servidor las rechaza.
    const payloadComponents = mappedComponents
      .map((c, rowIndex) => ({ ...c, quantity: Number(c.quantity), rowIndex }))
      .filter(c => c.refId && c.quantity);
    const componentRowIndexes = payloadComponents.map(c => c.rowIndex);

    const payloadRaw = {
      name,
      ingredients: [], // Always empty, we enable migration to 'components'
      components: payloadComponents.map(({ rowIndex, ...c }) => c),
      steps: steps.filter(s => s.trim() !== ''),
      servings: Number(servings),
      prepTime,
      tipoDeCocina: tipoDeCocina.map(t => t._id),
      restricciones: restricciones.map(r => r._id),
      categoriaRecetas: categoriaRecetas.map(c => c._id),
      // Siempre array (aunque esté vacío): con undefined el servidor mantenía los
      // valores anteriores y no había forma de quitar todas las etiquetas.
      momentoDelDia,
      tipoPlato,
      difficulty: difficulty || undefined,
      externalUrl: externalUrl || undefined,
      mainImage: mainImage || undefined,
      createdBy: createdBy || undefined,
      revisado: isAdmin ? revisado : undefined,
    };

    const payload = JSON.parse(JSON.stringify(payloadRaw));

    try {
      const url = isEditMode
        ? `${import.meta.env.VITE_API_URL}/recipes/${id}`
        : `${import.meta.env.VITE_API_URL}/recipes`;

      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setOpenSuccessDialog(true);
        setTimeout(() => {
          if (isEditMode) {
            navigate(`/recetas/${id}`);
          } else {
            navigate('/recetas');
          }
        }, 2000);
      } else {
        const errData = await res.json().catch(() => null);
        if (errData?.errors?.length) {
          const translated = translateServerErrors(errData.errors, componentRowIndexes);
          setFieldErrors(translated.fields);
          setMessage(buildProblemsMessage(translated.problems, 'El servidor ha rechazado estos datos:'));
        } else {
          setMessage(errData?.message || `No se pudo guardar la receta (error ${res.status}).`);
        }
        setOpenErrorDialog(true);
      }
    } catch (e) {
      console.error(e);
      setMessage('Error de conexión con el servidor.');
      setOpenErrorDialog(true);
    }
  };

  // --- Render Helpers ---

  const renderIngredientOption = (props, option) => (
    <li {...props} key={option._id}>
      <IngredientItem ingredient={option} isAdmin={false} showName={true} />
    </li>
  );

  const getSubRecipeTooltipData = (recipeId) => {
    const subRecipe = allRecipes.find(r => r._id === recipeId);
    if (!subRecipe) return null;

    const subItems = subRecipe.components && subRecipe.components.length > 0 ? subRecipe.components : subRecipe.ingredients;
    if (!subItems || subItems.length === 0) return "Sin ingredientes";

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5 }}>
          Ingredientes por ración ({subRecipe.servings || 1} rac. total):
        </Typography>
        <ul style={{ paddingLeft: '1rem', margin: 0, fontSize: '0.85rem' }}>
          {subItems.map((item, i) => {
            let name = "Desconocido";
            let unit = item.unit || 'g';
            let qty = item.quantity || 0;

            if (item.refModel === 'Ingredient' || item.ingredientId) {
              const id = item.refId || item.ingredientId;
              const ing = allIngredients.find(ing => ing._id === id);
              if (ing) name = ing.name;
            } else if (item.refModel === 'Recipe') {
              const id = item.refId;
              const r = allRecipes.find(rec => rec._id === id);
              if (r) name = `Sub: ${r.name}`;
            }
            const qtyPerServing = (qty / (subRecipe.servings || 1)).toFixed(1);
            const formattedQty = qtyPerServing.endsWith('.0') ? qtyPerServing.slice(0, -2) : qtyPerServing;

            return (
              <li key={i}>{name}: <b>{formattedQty} {unit}</b></li>
            );
          })}
        </ul>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#fff', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="800" color="#1e293b">
            {isEditMode ? 'Editar Receta' : 'Nueva Receta'}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} sx={{ borderRadius: '12px', px: 3 }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} startIcon={<SaveIcon />}
            sx={{ borderRadius: '12px', px: 3, bgcolor: '#667eea', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' }}>
            {isEditMode ? 'Actualizar Receta' : 'Guardar Receta'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} lg={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            <ImageUploadBox image={getPreviewImage(mainImage)}>
              <input
                accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.heic,.heif"
                id="mainImage-input"
                type="file"
                onChange={handleImageSelect}
                disabled={uploadingImage}
                style={{ display: 'none' }}
              />
              <Box
                component="label"
                htmlFor="mainImage-input"
                sx={{
                  width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 1, cursor: 'pointer',
                  '& .change-hint': { opacity: 0, transition: '0.2s' },
                  '&:hover .change-hint': { opacity: 1 }
                }}
              >
                {uploadingImage ? (
                  <CircularProgress size={32} sx={{ color: '#fff' }} />
                ) : !mainImage ? (
                  <>
                    <UploadFileIcon sx={{ fontSize: 40, color: '#64748b' }} />
                    <Typography variant="caption" color="#64748b" fontWeight={600}>Subir foto (se recorta a 4:3)</Typography>
                  </>
                ) : (
                  <Box className="change-hint" sx={{ bgcolor: 'rgba(15,23,42,0.65)', color: '#fff', px: 1.5, py: 0.6, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <UploadFileIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" fontWeight={600}>Cambiar foto</Typography>
                  </Box>
                )}
              </Box>
            </ImageUploadBox>
            {/* Basic Info */}
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <CardHeaderTitle><RestaurantMenuIcon sx={{ color: '#667eea' }} />Información Básica</CardHeaderTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={name}
                      onChange={e => { setName(e.target.value); clearFieldError('name'); }}
                      error={!!fieldErrors.name}
                      helperText={fieldErrors.name || ''}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Raciones"
                      type="number"
                      value={servings}
                      onChange={e => { setServings(e.target.value); clearFieldError('servings'); }}
                      error={!!fieldErrors.servings}
                      helperText={fieldErrors.servings || ''}
                    />
                  </Grid>
                  <Grid item xs={12}><TextField fullWidth label="URL (Opcional)" value={externalUrl} onChange={e => setExternalUrl(e.target.value)} /></Grid>
                  {isAdmin && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={revisado} onChange={(e) => setRevisado(e.target.checked)} color="primary" />}
                        label={<Typography fontWeight="600" color="text.secondary">Receta Revisada <VerifiedIcon fontSize="small" color="primary" sx={{ verticalAlign: 'middle', ml: 0.5 }} /></Typography>}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </StyledCard>
            {/* Details */}
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <CardHeaderTitle><LocalDiningIcon sx={{ color: '#f6ad55' }} />Detalles</CardHeaderTitle>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" gap={1}>
                      <TextField label="H" type="number" size="small" value={prepTime.hours} onChange={e => setPrepTime({ ...prepTime, hours: e.target.value })} />
                      <TextField label="M" type="number" size="small" value={prepTime.minutes} onChange={e => setPrepTime({ ...prepTime, minutes: e.target.value })} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Dificultad</InputLabel>
                      <Select value={difficulty} label="Dificultad" onChange={e => setDifficulty(e.target.value)}>
                        <MenuItem value="Baja">Baja</MenuItem><MenuItem value="Media">Media</MenuItem><MenuItem value="Alta">Alta</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Momento del dia (Chips) */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" mb={1} display="block">Momento del día</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {MOMENTOS_DEL_DIA.map(m => (
                        <ToggleChip
                          key={m}
                          label={m}
                          selected={momentoDelDia.includes(m)}
                          onClick={() => toggleSelection(momentoDelDia, m, setMomentoDelDia)}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Tipo de Plato (Chips) */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" mb={1} display="block">Tipo de Plato</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {TIPOS_DE_PLATO.map(t => (
                        <ToggleChip
                          key={t}
                          label={t}
                          selected={tipoPlato.includes(t)}
                          onClick={() => toggleSelection(tipoPlato, t, setTipoPlato)}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Autocomplete
                        multiple
                        fullWidth
                        options={allTipoDeCocina}
                        getOptionLabel={o => o?.name || ''}
                        isOptionEqualToValue={(option, val) => option?._id === val?._id}
                        value={tipoDeCocina}
                        onChange={(_, v) => setTipoDeCocina(v)}
                        renderInput={p => <TextField {...p} label="Tipo de Cocina" size="small" />}
                      />
                      {isAdmin && (
                        <IconButton onClick={() => openTaxDialog('cuisine', 'tipoDeCocina', 'Nuevo Tipo de Cocina', setAllTipoDeCocina, allTipoDeCocina, 'name')} sx={{ bgcolor: '#eff6ff', borderRadius: '8px' }}>
                          <AddIcon color="primary" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Autocomplete
                        multiple
                        fullWidth
                        options={allRestricciones}
                        getOptionLabel={o => o.name}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        value={restricciones}
                        onChange={(_, v) => setRestricciones(v)}
                        renderInput={p => <TextField {...p} label="Restricciones" size="small" />}
                      />
                      {isAdmin && (
                        <IconButton onClick={() => openTaxDialog('restriction', 'restricciones', 'Nueva Restricción', setAllRestricciones, allRestricciones, 'name')} sx={{ bgcolor: '#eff6ff', borderRadius: '8px' }}>
                          <AddIcon color="primary" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Autocomplete
                        multiple
                        fullWidth
                        options={allCategoriaRecetas}
                        getOptionLabel={o => o?.nombre || ''}
                        isOptionEqualToValue={(option, val) => option?._id === val?._id}
                        value={categoriaRecetas}
                        onChange={(_, v) => setCategoriaRecetas(v)}
                        renderInput={p => <TextField {...p} label="Categorias" size="small" />}
                      />
                      {isAdmin && (
                        <IconButton onClick={() => openTaxDialog('category', 'categoriaRecetas', 'Nueva Categoría', setAllCategoriaRecetas, allCategoriaRecetas, 'nombre')} sx={{ bgcolor: '#eff6ff', borderRadius: '8px' }}>
                          <AddIcon color="primary" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Box>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} lg={8}>
          <Box display="flex" flexDirection="column" gap={3}>

            {/* Components (Ingredients & Subrecipes) */}
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <CardHeaderTitle sx={{ mb: 0 }}><ShoppingBasketIcon sx={{ color: '#667eea', mr: 1 }} />Ingredientes y Sub-recetas</CardHeaderTitle>
                    <Typography variant="caption" color="text.secondary">Define la estructura de tu receta añadiendo ingredientes individuales u otras recetas completas.</Typography>
                  </Box>
                  <Chip label={`${components.length} items`} size="small" color="primary" variant="outlined" />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box display="flex" flexDirection="column" gap={1.5}>
                  {components.map((comp, idx) => {
                    const isRecipe = comp.refModel === 'Recipe';
                    // Errores devueltos por la validación o por el servidor para esta fila
                    const rowQuantityError = fieldErrors[`components.${idx}.quantity`];
                    const rowUnitError = fieldErrors[`components.${idx}.unit`];
                    const rowRefError = fieldErrors[`components.${idx}.refId`] || fieldErrors[`components.${idx}.refModel`];

                    // Ingrediente seleccionado y su referencia de gramos por unidad
                    const selectedIngredient = !isRecipe ? allIngredients.find(i => i._id === comp.refId) : null;
                    // Mostramos la referencia si la unidad no es gramos (gramsPerUnit > 0)
                    // o si es gramos pero el valor es mayor que 1 (caso informativo).
                    const hasGramsRef = selectedIngredient && (
                      (selectedIngredient.standardUnit !== 'gramos' && selectedIngredient.gramsPerUnit > 0)
                      || selectedIngredient.gramsPerUnit > 1
                    );
                    const unitLabel = selectedIngredient?.standardUnit && selectedIngredient.standardUnit !== 'gramos'
                      ? selectedIngredient.standardUnit
                      : 'ud';
                    // El total en gramos solo tiene sentido cuando la cantidad se expresa en unidades.
                    const totalGrams = hasGramsRef && comp.quantity && comp.unit && comp.unit !== 'gramos'
                      ? Number(comp.quantity) * selectedIngredient.gramsPerUnit
                      : null;

                    return (
                      <ComponentRow container key={idx} alignItems="flex-start" spacing={1}>
                        {/* Type Select */}
                        <Grid item xs={12} sm={2.5}>
                          <FormControl fullWidth size="small" variant="outlined">
                            <InputLabel>Tipo</InputLabel>
                            <Select
                              value={comp.refModel}
                              label="Tipo"
                              onChange={e => handleComponentChange(idx, 'refModel', e.target.value)}
                              sx={{ borderRadius: '10px', bgcolor: 'white' }}
                            >
                              <MenuItem value="Ingredient">
                                <Box display="flex" alignItems="center" gap={1}>
                                  <LocalDiningIcon fontSize="small" sx={{ color: '#667eea' }} /> Ingrediente
                                </Box>
                              </MenuItem>
                              <MenuItem value="Recipe">
                                <Box display="flex" alignItems="center" gap={1}>
                                  <BlenderIcon fontSize="small" sx={{ color: '#f6ad55' }} /> Sub-receta
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Autocomplete */}
                        <Grid item xs={12} sm={4.5}>
                          {comp.refModel === 'Ingredient' ? (
                            <Autocomplete
                              key={`ing-${idx}`}
                              freeSolo
                              options={allIngredients}
                              getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name || ''))}
                              isOptionEqualToValue={(option, val) => option?._id === val?._id}
                              value={allIngredients.find(i => i._id === comp.refId) || (comp.proposedName ? { name: comp.proposedName } : null)}
                              inputValue={comp.inputValue != null ? comp.inputValue : (allIngredients.find(i => i._id === comp.refId)?.name || '')}
                              onInputChange={(_, newInputValue) => {
                                const newComps = [...components];
                                newComps[idx].inputValue = newInputValue;
                                if (!newInputValue) {
                                  newComps[idx].proposedName = '';
                                  newComps[idx].refId = '';
                                  newComps[idx].proposedData = null;
                                } else {
                                  const match = allIngredients.find(i => i.name.toLowerCase() === newInputValue.toLowerCase());
                                  if (match) {
                                    newComps[idx].refId = match._id;
                                    newComps[idx].proposedName = '';
                                    // Only auto-set unit if not already set or generic
                                    if (match.standardUnit) {
                                      newComps[idx].unit = match.standardUnit;
                                    }
                                  } else {
                                    // If text no longer matches the refId, clear it
                                    const currentRef = allIngredients.find(i => i._id === newComps[idx].refId);
                                    if (currentRef && currentRef.name.toLowerCase() !== newInputValue.toLowerCase()) {
                                      newComps[idx].refId = '';
                                    }
                                  }
                                }
                                setComponents(newComps);
                              }}
                              onChange={(_, val) => {
                                if (typeof val === 'string') {
                                  // Check if string matches existing ingredient
                                  const match = allIngredients.find(i => i.name.toLowerCase() === val.toLowerCase());
                                  if (match) {
                                    handleComponentChange(idx, 'refId', match._id);
                                    handleComponentChange(idx, 'proposedName', '');
                                    if (match.standardUnit) {
                                      handleComponentChange(idx, 'unit', match.standardUnit);
                                    }
                                  } else {
                                    handleComponentChange(idx, 'proposedName', val);
                                    handleComponentChange(idx, 'refId', '');
                                  }
                                } else if (val && val._id) {
                                  handleComponentChange(idx, 'refId', val._id);
                                  handleComponentChange(idx, 'proposedName', '');
                                  if (val.standardUnit) {
                                    handleComponentChange(idx, 'unit', val.standardUnit);
                                  }
                                } else {
                                  handleComponentChange(idx, 'refId', '');
                                }
                              }}
                              renderInput={(p) => {
                                const selectedOption = allIngredients.find(i => i._id === comp.refId);
                                // IsNew if: No refId AND (has text) AND (text doesn't match an existing ingredient)
                                const inputText = (comp.proposedName || comp.inputValue || '').toLowerCase();
                                const exactMatch = allIngredients.some(i => i.name.toLowerCase() === inputText);
                                const isNew = !comp.refId && inputText && !exactMatch;

                                return (
                                  <TextField
                                    {...p}
                                    label={isNew ? "Ingrediente (Nuevo/Sugerido)" : "Buscar ingrediente..."}
                                    size="small"
                                    color={isNew ? "warning" : "primary"}
                                    error={!!rowRefError}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
                                    InputProps={{
                                      ...p.InputProps,
                                      startAdornment: (
                                        <>
                                          {selectedOption?.revisado && (
                                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
                                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                            </div>
                                          )}
                                          {p.InputProps.startAdornment}
                                        </>
                                      ),
                                      endAdornment: (
                                        <>
                                          {isNew && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="warning"
                                              sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 2, mr: 2 }}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const pd = comp.proposedData || {};
                                                const foundCat = categories.find(c =>
                                                  c.nombre && pd.categoria &&
                                                  c.nombre.toLowerCase().includes(pd.categoria.toLowerCase())
                                                );

                                                setIngrediente({
                                                  name: comp.proposedName || comp.inputValue || '',
                                                  kilocalories: pd.kilocalories || '',
                                                  protein: pd.protein || '',
                                                  carbs: pd.carbs || '',
                                                  fat: pd.fat || '',
                                                  categoria: foundCat ? foundCat._id : '',
                                                  subcategoria: '',
                                                  standardUnit: pd.standardUnit || comp.unit || 'gramos',
                                                  gramsPerUnit: 1,
                                                  createdBy: ''
                                                });
                                                setOpenDialog(true);
                                              }}
                                            >
                                              Crear
                                            </Button>
                                          )}
                                          {p.InputProps.endAdornment}
                                        </>
                                      )
                                    }}
                                  />
                                );
                              }}
                              renderOption={renderIngredientOption}
                            />
                          ) : (
                            <Autocomplete
                              key={`rec-${idx}`}
                              options={allRecipes.filter(r => r && r.name && r._id !== id)}
                              getOptionLabel={(o) => o?.name || ''}
                              isOptionEqualToValue={(option, val) => option?._id === val?._id}
                              value={allRecipes.find(r => r._id === comp.refId) || null}
                              onChange={(_, val) => handleComponentChange(idx, 'refId', val?._id)}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  {option.revisado && (
                                    <ListItemIcon style={{ minWidth: '30px' }}>
                                      <CheckCircleIcon sx={{ fontSize: '1.2rem', color: '#4caf50' }} />
                                    </ListItemIcon>
                                  )}
                                  <ListItemText primary={option.name} />
                                </li>
                              )}
                              renderInput={(p) => {
                                const selectedOption = allRecipes.find(r => r._id === comp.refId);
                                return (
                                  <TextField
                                    {...p}
                                    label="Buscar receta..."
                                    size="small"
                                    error={!!rowRefError}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
                                    InputProps={{
                                      ...p.InputProps,
                                      startAdornment: (
                                        <>
                                          {selectedOption?.revisado && (
                                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
                                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                            </div>
                                          )}
                                          {p.InputProps.startAdornment}
                                        </>
                                      )
                                    }}
                                  />
                                );
                              }}
                            />
                          )}
                        </Grid>

                        {/* Qty with Tooltip for Recipe */}
                        <Grid item xs={6} sm={2}>
                          {isRecipe && comp.refId ? (
                            <Tooltip title={getSubRecipeTooltipData(comp.refId)} placement="top" arrow>
                              <TextField
                                type="number"
                                label="Cant."
                                size="small"
                                required
                                error={!!rowQuantityError || (!!comp.refId && (comp.quantity === '' || Number(comp.quantity) <= 0))}
                                helperText={rowQuantityError || ''}
                                value={comp.quantity}
                                onChange={e => handleComponentChange(idx, 'quantity', e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
                                InputProps={{
                                  endAdornment: <InfoOutlinedIcon fontSize="small" color="action" sx={{ opacity: 0.5 }} />
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <TextField
                              type="number"
                              label="Cant."
                              size="small"
                              required
                              error={!!rowQuantityError || (!!comp.refId && (comp.quantity === '' || Number(comp.quantity) <= 0))}
                              helperText={rowQuantityError || ''}
                              value={comp.quantity}
                              onChange={e => handleComponentChange(idx, 'quantity', e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
                            />
                          )}
                          {/* Referencia de gramos por unidad */}
                          {hasGramsRef && (
                            <Box display="flex" alignItems="center" gap={0.25} sx={{ mt: 0.5, color: '#64748b' }}>
                              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
                                1 {unitLabel} ≈ {selectedIngredient.gramsPerUnit} g
                                {totalGrams ? ` · ${Math.round(totalGrams)} g` : ''}
                              </Typography>
                            </Box>
                          )}
                        </Grid>

                        {/* Unit - Disabled/Fixed for Recipe */}
                        <Grid item xs={6} sm={2}>
                          <FormControl fullWidth size="small" error={!!rowUnitError}>
                            <InputLabel>Unidad</InputLabel>
                            <Select
                              value={comp.unit}
                              label="Unidad"
                              onChange={e => handleComponentChange(idx, 'unit', e.target.value)}
                              sx={{ borderRadius: '10px', bgcolor: 'white' }}
                              disabled={isRecipe}
                            >
                              {isRecipe ? (
                                <MenuItem value="raciones">Raciones</MenuItem>
                              ) : (
                                (() => {
                                  const selectedIng = allIngredients.find(i => i._id === comp.refId);
                                  const options = new Set(['gramos', 'uds']); // Default options
                                  if (selectedIng && selectedIng.standardUnit) {
                                    options.add(selectedIng.standardUnit.toLowerCase());
                                  }
                                  // Also keep current value if not in defaults
                                  if (comp.unit) options.add(comp.unit.toLowerCase());

                                  return Array.from(options).map(u => (
                                    <MenuItem key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</MenuItem>
                                  ));
                                })()
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={1} display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 40 }}>
                          <IconButton onClick={() => handleComponentDelete(idx)} sx={{ color: '#ef4444', bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' } }} size="small"><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Grid>
                      </ComponentRow>
                    );
                  })}
                </Box>

                <Box mt={3} display="flex" gap={2}>
                  <Button
                    variant="dashed"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddComponent}
                    sx={{ flex: 1, border: '1px dashed #cbd5e1', color: '#64748b', borderRadius: '12px', height: '48px', '&:hover': { border: '1px dashed #94a3b8', bgcolor: '#f8fafc' } }}
                  >
                    Añadir Componente
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenDialog(true)}
                    sx={{ borderRadius: '12px', height: '48px', textTransform: 'none' }}
                  >
                    Crear Ingrediente
                  </Button>
                </Box>

                {totalMacronutrients && (
                  <Box mt={4} p={2} bgcolor="#f8fafc" borderRadius="16px" display="flex" justifyContent="space-around" alignItems="center">
                    <MacroChip color="#fee2e2">
                      <MacroIcon macro="kilocalories" size={28} style={{ marginBottom: 4 }} />
                      <span className="label">Calorías</span>
                      <span className="value">{Math.round(totalMacronutrients.kilocalories)} kcal</span>
                    </MacroChip>
                    <MacroChip color="#dbeafe">
                      <MacroIcon macro="protein" size={28} style={{ marginBottom: 4 }} />
                      <span className="label">Proteínas</span>
                      <span className="value">{Math.round(totalMacronutrients.protein)}g</span>
                    </MacroChip>
                    <MacroChip color="#ffedd5">
                      <MacroIcon macro="carbs" size={28} style={{ marginBottom: 4 }} />
                      <span className="label">Carbohidratos</span>
                      <span className="value">{Math.round(totalMacronutrients.carbs)}g</span>
                    </MacroChip>
                    <MacroChip color="#fce7f3">
                      <MacroIcon macro="fat" size={28} style={{ marginBottom: 4 }} />
                      <span className="label">Grasas</span>
                      <span className="value">{Math.round(totalMacronutrients.fat)}g</span>
                    </MacroChip>
                  </Box>
                )}
              </CardContent>
            </StyledCard>

            {/* Steps (Same as before) */}
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <CardHeaderTitle><FormatListNumberedIcon sx={{ color: '#667eea' }} />Pasos</CardHeaderTitle>
                <Box display="flex" flexDirection="column" gap={3}>
                  {steps.map((step, idx) => (
                    <Box key={idx} display="flex" gap={2}>
                      <StepNumber>{idx + 1}</StepNumber>
                      <Box flex={1}><TextField multiline minRows={2} fullWidth placeholder="Describe el paso..." value={step} onChange={e => handleStepChange(idx, e.target.value)} InputProps={{ sx: { borderRadius: '16px', bgcolor: '#f8fafc' } }} /></Box>
                      <IconButton onClick={() => handleRemoveStep(idx)} sx={{ height: 40 }} size="small"><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Box>
                  ))}
                </Box>
                <Button fullWidth startIcon={<AddCircleOutlineIcon />} onClick={handleAddStep} sx={{ mt: 3, border: '1px dashed #cbd5e1', color: '#64748b', borderRadius: '12px', py: 1.5 }}>Añadir paso</Button>
              </CardContent>
            </StyledCard>
          </Box>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <ImageCropDialog
        open={openCropDialog}
        imageSrc={cropSrc}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />

      <SuccessDialog open={openSuccessDialog} message={isEditMode ? "Receta actualizada con éxito..." : "Receta guardada con éxito, redirigiendo..."} />

      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#ef4444', fontWeight: 'bold' }}>No se pudo completar la acción</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Nuevo Ingrediente</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}><TextField fullWidth label="Nombre" value={ingrediente.name} onChange={e => setIngrediente({ ...ingrediente, name: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={ingrediente.categoria} label="Categoría" onChange={e => setIngrediente({ ...ingrediente, categoria: e.target.value, subcategoria: '' })}>
                  {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Subcategoría</InputLabel>
                <Select
                  value={ingrediente.subcategoria}
                  label="Subcategoría"
                  onChange={e => setIngrediente({ ...ingrediente, subcategoria: e.target.value })}
                  disabled={!ingrediente.categoria}
                >
                  {categories.find(c => c._id === ingrediente.categoria)?.subcategorias.map(sc => (
                    <MenuItem key={sc} value={sc}>{sc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Unidad</InputLabel>
                <Select value={ingrediente.standardUnit} label="Unidad" onChange={e => setIngrediente({ ...ingrediente, standardUnit: e.target.value })}>
                  <MenuItem value="gramos">Gramos</MenuItem><MenuItem value="uds">Unidades</MenuItem>
                  <MenuItem value="cucharadas">Cucharadas</MenuItem><MenuItem value="cucharaditas">Cucharaditas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Gramos por unidad"
                type="number"
                value={ingrediente.gramsPerUnit}
                onChange={e => setIngrediente({ ...ingrediente, gramsPerUnit: e.target.value })}
              />
            </Grid>
            <Grid item xs={3}><TextField label="Kcal" type="number" value={ingrediente.kilocalories} onChange={e => setIngrediente({ ...ingrediente, kilocalories: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField label="Prot" type="number" value={ingrediente.protein} onChange={e => setIngrediente({ ...ingrediente, protein: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField label="Carb" type="number" value={ingrediente.carbs} onChange={e => setIngrediente({ ...ingrediente, carbs: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField label="Gras" type="number" value={ingrediente.fat} onChange={e => setIngrediente({ ...ingrediente, fat: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={allIngredientCategories}
                getOptionLabel={(option) => option.name}
                value={ingrediente.ingredientCategories}
                onChange={(_, newValue) => setIngrediente({ ...ingrediente, ingredientCategories: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Categorías de Ingredientes" placeholder="Seleccionar..." />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={allAllergens}
                getOptionLabel={(option) => option.name}
                value={ingrediente.allergens}
                onChange={(_, newValue) => setIngrediente({ ...ingrediente, allergens: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Alérgenos" placeholder="Seleccionar..." />
                )}
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={ingrediente.revisado} onChange={e => setIngrediente({ ...ingrediente, revisado: e.target.checked })} />}
                  label="Revisado"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateIngredient}>Crear</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}><Alert severity="success">Ingrediente creado.</Alert></Snackbar>

      {/* Taxonomy Creation Dialog */}
      <TaxonomyDialog
        open={openTaxonomyDialog.open}
        onClose={() => setOpenTaxonomyDialog({ ...openTaxonomyDialog, open: false })}
        title={openTaxonomyDialog.title}
        endpoint={openTaxonomyDialog.endpoint}
        items={openTaxonomyDialog.currentList}
        setItems={openTaxonomyDialog.setter}
        fieldName={openTaxonomyDialog.field}
      />
    </PageContainer>
  );
};
export default NuevaReceta;
